// buildStory — pure derivation for the Strategy Story presentation view.
//
// Takes the scenarios the advisor has ticked into the story (baseline first,
// in comparison order) and returns one card per scenario. Each card's moves
// are the diff vs the PREVIOUS included scenario — the chapter's new plays —
// while the payoff figure is always measured against the baseline.
//
// HARD RULE: every number on a card is a number the calculator already
// outputs (stored input/results). Nothing is derived that the app doesn't
// already show elsewhere, and nothing here calls the engine. The payoff uses
// the same est.-future-value-economic-difference formula as pdfExport /
// excelExport, so the story can never disagree with the table behind it.

import type { TaxScenario, TaxScenarioInput, TaxResults } from "@/tax/types";
import { PAYOFF_HORIZON_FLOOR_YEARS } from "@/tax/strategyJourney";

export interface StoryMove {
  label: string;
  /** Absolute dollar change vs the previous chapter. */
  amount: number;
  /** Whether this move pushes the year's tax up (warm) or down (cool). */
  direction: "up" | "down";
}

export interface StoryCard {
  scenarioId: string;
  stepNo: number;
  isBaseline: boolean;
  /** "Where You Are" for baseline; the strategy suffix otherwise. */
  title: string;
  moves: StoryMove[];
  taxThisYear: number;
  /**
   * Outcome deltas are ALWAYS measured against the baseline ("doing
   * nothing"), never the previous chapter — the client cares what the whole
   * play does vs their life today. Null on the baseline card.
   */
  taxDeltaVsBase: number | null;
  taxableIncome: number;
  taxableIncomeDeltaVsBase: number | null;
  effectiveRate: number;
  effectiveRateDeltaVsBase: number | null;
  deductionUsed: number;
  /** Condition-gated factual sentences (only true statements render). */
  notes: string[];
  /** Est. future value economic difference vs baseline (exports' formula). Null on baseline. */
  fvEconDiff: number | null;
}

export interface BuildStory {
  cards: StoryCard[];
  /** Largest tax-this-year across ALL comparison scenarios — shared bar scale. */
  maxTax: number;
  growthYears: number;
}

/** Same helper as ComparisonTable#strategySuffix — last parenthesized group. */
function strategySuffix(name: string): string {
  const m = name.match(/\(([^)]+)\)\s*$/);
  return m ? m[1] : name;
}

// Input fields that constitute "moves" between chapters, with display labels.
// direction 'up' = adding this raises what gets taxed; the alt investment is
// the one 'down' move (it buys a deduction).
const MOVE_FIELDS: { key: string; label: string; kind: "income" | "deduction" }[] = [
  { key: "rothConversionAmount", label: "Roth Conversion", kind: "income" },
  { key: "iraDistributionConversion", label: "IRA Distribution", kind: "income" },
  { key: "longTermCapitalGains", label: "Long-Term Capital Gains", kind: "income" },
  { key: "shortTermCapitalGains", label: "Short-Term Capital Gains", kind: "income" },
  { key: "wages", label: "Wages", kind: "income" },
  { key: "spouseWages", label: "Spouse Wages", kind: "income" },
  { key: "interestIncome", label: "Interest", kind: "income" },
  { key: "ordinaryDividends", label: "Ordinary Dividends", kind: "income" },
  { key: "qualifiedDividends", label: "Qualified Dividends", kind: "income" },
  { key: "grossSocialSecurity", label: "Social Security", kind: "income" },
  { key: "pensionSum", label: "Pension", kind: "income" },
  { key: "otherIncome", label: "Other Income", kind: "income" },
  { key: "alternativeInvestmentAmount", label: "Alt Investment", kind: "deduction" },
];

function diffMoves(curr: TaxScenario, prev: TaxScenario): StoryMove[] {
  const moves: StoryMove[] = [];
  for (const f of MOVE_FIELDS) {
    const currVal = (curr.input as unknown as Record<string, number>)[f.key] ?? 0;
    const prevVal = (prev.input as unknown as Record<string, number>)[f.key] ?? 0;
    const delta = currVal - prevVal;
    if (Math.abs(delta) < 0.005) continue;
    // Income added heats tax up; income removed cools it. The alt investment
    // is inverted: adding it buys a deduction (cool), removing it heats.
    const addedHeats = f.kind === "income";
    const direction: StoryMove["direction"] =
      delta > 0 === addedHeats ? "up" : "down";
    moves.push({ label: f.label, amount: Math.abs(delta), direction });
  }
  return moves;
}

const fmtUSD = (v: number) =>
  "$" + Math.abs(Math.round(v)).toLocaleString("en-US");

/** Condition-gated notes — every sentence is computed from stored fields. */
function buildNotes(s: TaxScenario, prev: TaxScenario, growthYears: number): string[] {
  const notes: string[] = [];
  const roth = s.input.rothConversionAmount ?? 0;
  const prevRoth = prev.input.rothConversionAmount ?? 0;
  if (roth > 0 && roth !== prevRoth && s.results.rothFutureValue > 0) {
    notes.push(
      `Buys ${fmtUSD(s.results.rothFutureValue)} growing tax-free by Year ${growthYears}.`,
    );
  }

  const alt = s.input.alternativeInvestmentAmount ?? 0;
  const prevAlt = prev.input.alternativeInvestmentAmount ?? 0;
  if (alt > 0 && alt !== prevAlt) {
    const implied = alt * (s.input.hpeDeductionMultiplier ?? 0);
    if (implied - s.results.hpeImpliedDeduction > 0.5) {
      notes.push(
        `${s.input.hpeDeductionMultiplier}× implies ${fmtUSD(implied)}; limited to ${fmtUSD(s.results.hpeImpliedDeduction)} by AGI caps.`,
      );
    }
    if (s.results.hpeNetCashReturned > 0) {
      notes.push(
        `Alt investment cash back: ${fmtUSD(s.results.hpeNetCashReturned)} (hypothetical).`,
      );
    }
  }
  return notes;
}

/**
 * Derive the story cards from the included scenarios.
 * @param included baseline first, then the ticked strategies in comparison order.
 * @param allScenarios every scenario in the comparison — used only for the
 *        shared bar scale so bars don't rescale as chips toggle.
 */
export function deriveBuildStory(
  included: TaxScenario[],
  allScenarios?: TaxScenario[],
): BuildStory {
  const scaleSet = allScenarios?.length ? allScenarios : included;
  const maxTax = Math.max(1, ...scaleSet.map((s) => s.results.totalTaxLiability));
  const growthYears = Math.max(PAYOFF_HORIZON_FLOOR_YEARS, ...scaleSet.map((s) => s.input.rothGrowthYears || 0));
  const base = included[0];

  const cards: StoryCard[] = included.map((s, i) => {
    const fvEconDiff =
      i === 0
        ? null
        : s.results.hpeFutureValue +
          s.results.estimatedFutureTaxAvoided -
          (s.results.totalEconomicCost - base.results.totalEconomicCost);
    return {
      scenarioId: s.id,
      stepNo: i + 1,
      isBaseline: i === 0,
      title: i === 0 ? "Where You Are" : strategySuffix(s.input.name),
      // Moves are measured against the BASELINE, not the previous chapter —
      // each pill shows the full lever this plan applies vs doing nothing
      // (a $360K alt reads $360K, never a $80K delta from some other scenario).
      moves: i === 0 ? [] : diffMoves(s, base),
      taxThisYear: s.results.totalTaxLiability,
      taxDeltaVsBase: i === 0 ? null : s.results.totalTaxLiability - base.results.totalTaxLiability,
      taxableIncome: s.results.taxableIncome,
      taxableIncomeDeltaVsBase: i === 0 ? null : s.results.taxableIncome - base.results.taxableIncome,
      effectiveRate: s.results.effectiveTaxRate,
      effectiveRateDeltaVsBase: i === 0 ? null : s.results.effectiveTaxRate - base.results.effectiveTaxRate,
      deductionUsed: s.results.totalDeduction,
      notes: i === 0 ? [] : buildNotes(s, base, growthYears),
      fvEconDiff,
    };
  });

  return { cards, maxTax, growthYears };
}

// ── Step-by-step story (the Build) ───────────────────────────────────────
// Decomposes a single plan into its levers and produces the cumulative
// "what if" inputs for each step. The intermediate steps' RESULTS come from
// the same calculate-tax edge function every save uses (injected as `calc`
// so tests can stub it); the FINAL step always uses the scenario's stored
// results, so the story can never disagree with the comparison table.

// Story order: conversions first, ordinary income, then gains, alt last
// (the cool-down finale — matches how the plays are told).
const CHAIN_ORDER = [
  "rothConversionAmount",
  "iraDistributionConversion",
  "wages",
  "spouseWages",
  "interestIncome",
  "ordinaryDividends",
  "qualifiedDividends",
  "grossSocialSecurity",
  "pensionSum",
  "otherIncome",
  "shortTermCapitalGains",
  "longTermCapitalGains",
  "alternativeInvestmentAmount",
] as const;

const STEP_TITLES: Record<string, string> = {
  rothConversionAmount: "Add the Roth Conversion",
  iraDistributionConversion: "Add the IRA Distribution",
  longTermCapitalGains: "Add the Long-Term Gains",
  shortTermCapitalGains: "Add the Short-Term Gains",
  alternativeInvestmentAmount: "Add the Alt Investment",
};

const RECAP_LABELS: Record<string, string> = {
  rothConversionAmount: "Roth",
  iraDistributionConversion: "IRA",
  longTermCapitalGains: "LTCG",
  shortTermCapitalGains: "STCG",
  alternativeInvestmentAmount: "Alt",
};

const compact = (v: number) =>
  v >= 1_000_000
    ? `$${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`
    : v >= 1000
      ? `$${Math.round(v / 1000)}K`
      : `$${Math.round(v)}`;

export interface LeverStep {
  key: string;
  title: string;
  /** Cumulative recap, e.g. "Roth $200K + LTCG $200K". */
  recap: string;
  move: StoryMove;
  /** The cumulative "what if" input for this step. Final step === scenario input. */
  input: TaxScenarioInput;
}

/**
 * Pure: list the plan's levers (vs baseline) in story order, each with the
 * cumulative input. Built from the SCENARIO's input with not-yet-applied
 * levers reset to baseline values — so non-lever fields (growth years,
 * fund vintage, deductions, …) carry through every step, and the final
 * step's input is exactly the scenario's own input.
 */
export function buildLeverChain(baseInput: TaxScenarioInput, targetInput: TaxScenarioInput): LeverStep[] {
  const fieldByKey = new Map(MOVE_FIELDS.map((f) => [f.key, f] as const));
  const target = targetInput as unknown as Record<string, number>;
  const base = baseInput as unknown as Record<string, number>;

  const levers = CHAIN_ORDER.filter((key) => Math.abs((target[key] ?? 0) - (base[key] ?? 0)) >= 0.005);
  const steps: LeverStep[] = [];
  const recapParts: string[] = [];

  levers.forEach((key, idx) => {
    const f = fieldByKey.get(key)!;
    const delta = (target[key] ?? 0) - (base[key] ?? 0);
    const input = { ...targetInput } as unknown as Record<string, number>;
    // Reset levers that come AFTER this step back to their baseline values.
    for (const later of levers.slice(idx + 1)) input[later] = base[later] ?? 0;
    const addedHeats = f.kind === "income";
    recapParts.push(`${RECAP_LABELS[key] ?? f.label} ${compact(Math.abs(delta))}`);
    steps.push({
      key,
      title: STEP_TITLES[key] ?? `Add ${f.label}`,
      recap: recapParts.join(" + "),
      move: { label: f.label, amount: Math.abs(delta), direction: delta > 0 === addedHeats ? "up" : "down" },
      input: input as unknown as TaxScenarioInput,
    });
  });
  return steps;
}

export interface ComputedStep {
  stepNo: number; // 2-based; step 1 is the baseline card
  title: string;
  recap: string;
  move: StoryMove;
  taxThisYear: number;
  deltaVsPrev: number;
  taxableIncome: number;
  effectiveRate: number;
  deductionUsed: number;
  notes: string[];
}

/**
 * Resolve the chain into rendered steps. Intermediates via `calc` (the
 * calculate-tax edge function); the final step uses stored results.
 */
export async function deriveStorySteps(
  baseline: TaxScenario,
  scenario: TaxScenario,
  calc: (input: TaxScenarioInput) => Promise<TaxResults>,
): Promise<ComputedStep[]> {
  const chain = buildLeverChain(baseline.input, scenario.input);
  if (chain.length === 0) return [];
  const growthYears = Math.max(PAYOFF_HORIZON_FLOOR_YEARS, scenario.input.rothGrowthYears || 0);

  const intermediates = await Promise.all(chain.slice(0, -1).map((s) => calc(s.input)));
  const resultsList: TaxResults[] = [...intermediates, scenario.results];

  let prevTax = baseline.results.totalTaxLiability;
  return chain.map((s, i) => {
    const r = resultsList[i];
    const notes: string[] = [];
    if (s.key === "rothConversionAmount" && r.rothFutureValue > 0) {
      notes.push(`Buys ${fmtUSD(r.rothFutureValue)} growing tax-free by Year ${growthYears}.`);
    }
    if (s.key === "longTermCapitalGains") {
      notes.push(`Repositions ${fmtUSD(s.move.amount)} of appreciated assets at capital-gains rates, not ordinary rates.`);
    }
    if (s.key === "alternativeInvestmentAmount") {
      const alt = (s.input as unknown as Record<string, number>).alternativeInvestmentAmount ?? 0;
      const implied = alt * (scenario.input.hpeDeductionMultiplier ?? 0);
      if (implied - r.hpeImpliedDeduction > 0.5) {
        notes.push(
          `${scenario.input.hpeDeductionMultiplier}× implies ${fmtUSD(implied)}; limited to ${fmtUSD(r.hpeImpliedDeduction)} by AGI caps.`,
        );
      }
      if (r.hpeNetCashReturned > 0) {
        notes.push(
          `Alt investment cash back: ${fmtUSD(r.hpeNetCashReturned)} (hypothetical).`,
        );
      }
    }
    const step: ComputedStep = {
      stepNo: i + 2,
      title: s.title,
      recap: s.recap,
      move: s.move,
      taxThisYear: r.totalTaxLiability,
      deltaVsPrev: r.totalTaxLiability - prevTax,
      taxableIncome: r.taxableIncome,
      effectiveRate: r.effectiveTaxRate,
      deductionUsed: r.totalDeduction,
      notes,
    };
    prevTax = r.totalTaxLiability;
    return step;
  });
}
